import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Plus,
  Trash2
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import procurementServiceMasterService from '../../services/procurementServiceMasterService'
import supplierMasterService from '../../services/supplierMasterService'

const ProcurementServiceMasterForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const serviceData = location.state?.serviceData

  const [formData, setFormData] = useState({
    serviceCode: '',
    serviceName: '',
    serviceDescription: '',
    category: '',
    serviceType: 'Professional',
    unitOfMeasurement: 'Hour',
    pricing: {
      basePrice: 0,
      currency: 'INR',
      costPrice: 0,
      minimumOrder: 0,
      discount: 0,
      taxRate: 0,
      taxType: 'GST'
    },
    serviceSpecifications: {
      deliverables: '',
      duration: 0,
      durationUnit: 'Days',
      serviceLevel: 'Standard',
      responseTime: 0,
      supportHours: ''
    },
    preferredSuppliers: [],
    taxInformation: {
      sacCode: '',
      gstRate: 0,
      igstRate: 0,
      cgstRate: 0,
      sgstRate: 0
    },
    status: 'Active',
    notes: ''
  })

  const [suppliers, setSuppliers] = useState([])
  const [isLoadingServiceCode, setIsLoadingServiceCode] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [newSupplier, setNewSupplier] = useState({
    supplier: '',
    supplierName: '',
    supplierCode: '',
    supplierPrice: 0,
    leadTime: 0,
    isPrimary: false
  })

  // Fetch next service code (only for new services)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchServiceCode = async () => {
        try {
          setIsLoadingServiceCode(true)
          const response = await procurementServiceMasterService.getNextServiceCode()
          if (response.success && response.data) {
            setFormData(prev => ({
              ...prev,
              serviceCode: response.data.serviceCode || response.data.formattedValue || 'PSVC0001'
            }))
          }
        } catch (error) {
          console.error('Error fetching service code:', error)
          setFormData(prev => ({ ...prev, serviceCode: 'PSVC0001' }))
        } finally {
          setIsLoadingServiceCode(false)
        }
      }
      fetchServiceCode()
    }
  }, [isEditMode, isViewMode])

  // Load suppliers
  useEffect(() => {
    loadSuppliers()
  }, [])

  // Load service data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && serviceData?._id) {
      loadServiceData(serviceData._id)
    }
  }, [isEditMode, isViewMode, serviceData])

  const loadSuppliers = async () => {
    try {
      const response = await supplierMasterService.getAllSuppliers({ status: 'Active', limit: 1000 })
      if (response.success) {
        setSuppliers(response.data || [])
      }
    } catch (error) {
      console.error('Error loading suppliers:', error)
    }
  }

  const loadServiceData = async (id) => {
    try {
      setIsLoading(true)
      const response = await procurementServiceMasterService.getProcurementServiceById(id)
      if (response.success && response.data) {
        const service = response.data
        setFormData({
          serviceCode: service.serviceCode || '',
          serviceName: service.serviceName || '',
          serviceDescription: service.serviceDescription || '',
          category: service.category || '',
          serviceType: service.serviceType || 'Professional',
          unitOfMeasurement: service.unitOfMeasurement || 'Hour',
          pricing: service.pricing || {
            basePrice: 0,
            currency: 'INR',
            costPrice: 0,
            minimumOrder: 0,
            discount: 0,
            taxRate: 0,
            taxType: 'GST'
          },
          serviceSpecifications: service.serviceSpecifications || {
            deliverables: '',
            duration: 0,
            durationUnit: 'Days',
            serviceLevel: 'Standard',
            responseTime: 0,
            supportHours: ''
          },
          preferredSuppliers: service.preferredSuppliers || [],
          taxInformation: service.taxInformation || {
            sacCode: '',
            gstRate: 0,
            igstRate: 0,
            cgstRate: 0,
            sgstRate: 0
          },
          status: service.status || 'Active',
          notes: service.notes || ''
        })
      }
    } catch (error) {
      console.error('Error loading service data:', error)
      toast.error('Failed to load service data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name.startsWith('pricing.')) {
      const pricingField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        pricing: {
          ...prev.pricing,
          [pricingField]: type === 'number' ? parseFloat(value) || 0 : value
        }
      }))
    } else if (name.startsWith('serviceSpecifications.')) {
      const specField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        serviceSpecifications: {
          ...prev.serviceSpecifications,
          [specField]: type === 'number' ? parseFloat(value) || 0 : value
        }
      }))
    } else if (name.startsWith('taxInformation.')) {
      const taxField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        taxInformation: {
          ...prev.taxInformation,
          [taxField]: type === 'number' ? parseFloat(value) || 0 : value
        }
      }))
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const addSupplier = () => {
    if (!newSupplier.supplier) {
      toast.error('Please select a supplier')
      return
    }
    
    const selectedSupplier = suppliers.find(s => s._id === newSupplier.supplier)
    setFormData(prev => ({
      ...prev,
      preferredSuppliers: [...prev.preferredSuppliers, {
        ...newSupplier,
        supplierName: selectedSupplier?.supplierName || '',
        supplierCode: selectedSupplier?.supplierCode || ''
      }]
    }))
    
    setNewSupplier({
      supplier: '',
      supplierName: '',
      supplierCode: '',
      supplierPrice: 0,
      leadTime: 0,
      isPrimary: false
    })
  }

  const removeSupplier = (index) => {
    setFormData(prev => ({
      ...prev,
      preferredSuppliers: prev.preferredSuppliers.filter((_, i) => i !== index)
    }))
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.serviceName) {
        toast.error('Please fill in all required fields (Service Name)')
        return
      }

      setIsLoading(true)
      
      if (isEditMode && serviceData?._id) {
        const response = await procurementServiceMasterService.updateProcurementService(serviceData._id, formData)
        if (response.success) {
          toast.success('Procurement service updated successfully!')
          navigate('/procurement/procurement-service-master')
        } else {
          toast.error(response.message || 'Failed to update procurement service')
        }
      } else {
        const response = await procurementServiceMasterService.createProcurementService(formData)
        if (response.success) {
          toast.success('Procurement service created successfully!')
          navigate('/procurement/procurement-service-master')
        } else {
          toast.error(response.message || 'Failed to create procurement service')
        }
      }
    } catch (error) {
      console.error('Error saving procurement service:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save procurement service')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (isEditMode || isViewMode) {
      loadServiceData(serviceData._id)
    } else {
      setFormData(prev => ({
        ...prev,
        serviceName: '',
        serviceDescription: '',
        category: '',
        serviceType: 'Professional',
        unitOfMeasurement: 'Hour',
        pricing: {
          basePrice: 0,
          currency: 'INR',
          costPrice: 0,
          minimumOrder: 0,
          discount: 0,
          taxRate: 0,
          taxType: 'GST'
        },
        serviceSpecifications: {
          deliverables: '',
          duration: 0,
          durationUnit: 'Days',
          serviceLevel: 'Standard',
          responseTime: 0,
          supportHours: ''
        },
        preferredSuppliers: [],
        taxInformation: {
          sacCode: '',
          gstRate: 0,
          igstRate: 0,
          cgstRate: 0,
          sgstRate: 0
        },
        status: 'Active',
        notes: ''
      }))
      setNewSupplier({
        supplier: '',
        supplierName: '',
        supplierCode: '',
        supplierPrice: 0,
        leadTime: 0,
        isPrimary: false
      })
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Procurement', path: '/procurement' },
    { label: 'Services', path: '/procurement/procurement-service-master' },
    { 
      label: isEditMode ? 'Edit Service' : isViewMode ? 'View Service' : 'New Service', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (isLoading && (isEditMode || isViewMode)) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading service...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Service' : isViewMode ? 'View Service' : 'New Service'}</h1>
          <p>{isEditMode ? 'Update service information' : isViewMode ? 'View service information' : 'Create a new procurement service'}</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/procurement/procurement-service-master')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Service Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header"><h3>Basic Information</h3></div>
          {/* Row 1: Service Code, Service Name, Service Type, Status */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="serviceCode" className="form-label required">
                Service Code
              </label>
              <input
                type="text"
                id="serviceCode"
                name="serviceCode"
                value={isLoadingServiceCode ? 'Generating...' : formData.serviceCode}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="serviceName" className="form-label required">
                Service Name
              </label>
              <input
                type="text"
                id="serviceName"
                name="serviceName"
                value={formData.serviceName}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
                placeholder="e.g., IT Consulting Services"
              />
            </div>

            <div className="form-group">
              <label htmlFor="serviceType" className="form-label">
                Service Type
              </label>
              <select
                id="serviceType"
                name="serviceType"
                value={formData.serviceType}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Consulting">Consulting</option>
                <option value="Maintenance">Maintenance</option>
                <option value="Professional">Professional</option>
                <option value="IT Services">IT Services</option>
                <option value="Support">Support</option>
                <option value="Training">Training</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          {/* Row 2: Category, Unit of Measurement */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="category" className="form-label">
                Category
              </label>
              <input
                type="text"
                id="category"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Service category"
              />
            </div>

            <div className="form-group">
              <label htmlFor="unitOfMeasurement" className="form-label">
                Unit of Measurement
              </label>
              <select
                id="unitOfMeasurement"
                name="unitOfMeasurement"
                value={formData.unitOfMeasurement}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Hour">Hour</option>
                <option value="Day">Day</option>
                <option value="Month">Month</option>
                <option value="Project">Project</option>
                <option value="Unit">Unit</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          {/* Service Description */}
          <div className="form-group">
            <label htmlFor="serviceDescription" className="form-label">
              Description
            </label>
            <textarea
              id="serviceDescription"
              name="serviceDescription"
              value={formData.serviceDescription}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Service description..."
            />
          </div>

          {/* Pricing Information */}
          <div className="section-header"><h3>Pricing Information</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="pricing.basePrice" className="form-label">
                Base Price
              </label>
              <input
                type="number"
                id="pricing.basePrice"
                name="pricing.basePrice"
                value={formData.pricing.basePrice}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="pricing.costPrice" className="form-label">
                Cost Price
              </label>
              <input
                type="number"
                id="pricing.costPrice"
                name="pricing.costPrice"
                value={formData.pricing.costPrice}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="pricing.minimumOrder" className="form-label">
                Minimum Order
              </label>
              <input
                type="number"
                id="pricing.minimumOrder"
                name="pricing.minimumOrder"
                value={formData.pricing.minimumOrder}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="pricing.currency" className="form-label">
                Currency
              </label>
              <select
                id="pricing.currency"
                name="pricing.currency"
                value={formData.pricing.currency}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="pricing.discount" className="form-label">
                Discount (%)
              </label>
              <input
                type="number"
                id="pricing.discount"
                name="pricing.discount"
                value={formData.pricing.discount}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                max="100"
              />
            </div>

            <div className="form-group">
              <label htmlFor="pricing.taxRate" className="form-label">
                Tax Rate (%)
              </label>
              <input
                type="number"
                id="pricing.taxRate"
                name="pricing.taxRate"
                value={formData.pricing.taxRate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                max="100"
              />
            </div>

            <div className="form-group">
              <label htmlFor="pricing.taxType" className="form-label">
                Tax Type
              </label>
              <select
                id="pricing.taxType"
                name="pricing.taxType"
                value={formData.pricing.taxType}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="GST">GST</option>
                <option value="VAT">VAT</option>
                <option value="Sales Tax">Sales Tax</option>
                <option value="None">None</option>
              </select>
            </div>
          </div>

          {/* Service Specifications */}
          <div className="section-header"><h3>Service Specifications</h3></div>

          <div className="form-group">
            <label htmlFor="serviceSpecifications.deliverables" className="form-label">
              Deliverables
            </label>
            <textarea
              id="serviceSpecifications.deliverables"
              name="serviceSpecifications.deliverables"
              value={formData.serviceSpecifications.deliverables}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Describe service deliverables..."
            />
          </div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="serviceSpecifications.duration" className="form-label">
                Duration
              </label>
              <input
                type="number"
                id="serviceSpecifications.duration"
                name="serviceSpecifications.duration"
                value={formData.serviceSpecifications.duration}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
              />
            </div>

            <div className="form-group">
              <label htmlFor="serviceSpecifications.durationUnit" className="form-label">
                Duration Unit
              </label>
              <select
                id="serviceSpecifications.durationUnit"
                name="serviceSpecifications.durationUnit"
                value={formData.serviceSpecifications.durationUnit}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Hours">Hours</option>
                <option value="Days">Days</option>
                <option value="Weeks">Weeks</option>
                <option value="Months">Months</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="serviceSpecifications.serviceLevel" className="form-label">
                Service Level
              </label>
              <select
                id="serviceSpecifications.serviceLevel"
                name="serviceSpecifications.serviceLevel"
                value={formData.serviceSpecifications.serviceLevel}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Basic">Basic</option>
                <option value="Standard">Standard</option>
                <option value="Premium">Premium</option>
                <option value="Enterprise">Enterprise</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="serviceSpecifications.responseTime" className="form-label">
                Response Time (Hours)
              </label>
              <input
                type="number"
                id="serviceSpecifications.responseTime"
                name="serviceSpecifications.responseTime"
                value={formData.serviceSpecifications.responseTime}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="serviceSpecifications.supportHours" className="form-label">
              Support Hours
            </label>
            <input
              type="text"
              id="serviceSpecifications.supportHours"
              name="serviceSpecifications.supportHours"
              value={formData.serviceSpecifications.supportHours}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              placeholder="e.g., 9 AM - 6 PM IST, Monday to Friday"
            />
          </div>

          {/* Preferred Suppliers */}
          <div className="section-header"><h3>Preferred Suppliers</h3></div>

          {formData.preferredSuppliers.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              {formData.preferredSuppliers.map((supplier, index) => (
                <div key={index} style={{ 
                  marginBottom: '12px',
                  padding: '12px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#f8f9fa'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div>
                      <strong>{supplier.supplierName || supplier.supplierCode}</strong>
                      {supplier.isPrimary && (
                        <span className="status-badge active" style={{ marginLeft: '8px', fontSize: '12px' }}>
                          Primary
                        </span>
                      )}
                    </div>
                    {!isViewMode && (
                      <button
                        type="button"
                        onClick={() => removeSupplier(index)}
                        style={{
                          padding: '4px 8px',
                          border: 'none',
                          background: '#dc3545',
                          color: '#fff',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--color-text-secondary, #5B6478)' }}>
                    Price: {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(supplier.supplierPrice || 0)} | 
                    Lead Time: {supplier.leadTime || 0} days
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isViewMode && (
            <div style={{ marginBottom: '16px', padding: '16px', border: '1px solid #e9ecef', borderRadius: '6px' }}>
              <div className="grid-row-4" style={{ marginBottom: '12px' }}>
                <div className="form-group">
                  <label className="form-label">Supplier</label>
                  <select
                    value={newSupplier.supplier}
                    onChange={(e) => {
                      const selectedSupplier = suppliers.find(s => s._id === e.target.value)
                      setNewSupplier({
                        ...newSupplier,
                        supplier: e.target.value,
                        supplierName: selectedSupplier?.supplierName || '',
                        supplierCode: selectedSupplier?.supplierCode || ''
                      })
                    }}
                    className="form-input"
                  >
                    <option value="">Select Supplier</option>
                    {suppliers.map(supplier => (
                      <option key={supplier._id} value={supplier._id}>
                        {supplier.supplierName} ({supplier.supplierCode})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Supplier Price</label>
                  <input
                    type="number"
                    value={newSupplier.supplierPrice}
                    onChange={(e) => setNewSupplier({ ...newSupplier, supplierPrice: parseFloat(e.target.value) || 0 })}
                    className="form-input"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Lead Time (Days)</label>
                  <input
                    type="number"
                    value={newSupplier.leadTime}
                    onChange={(e) => setNewSupplier({ ...newSupplier, leadTime: parseFloat(e.target.value) || 0 })}
                    className="form-input"
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Primary</label>
                  <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                    <input
                      type="checkbox"
                      checked={newSupplier.isPrimary}
                      onChange={(e) => setNewSupplier({ ...newSupplier, isPrimary: e.target.checked })}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <label style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                      Primary supplier
                    </label>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={addSupplier}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  background: '#292752',
                  color: '#fff',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Plus size={16} />
                Add Supplier
              </button>
            </div>
          )}

          {/* Tax Information */}
          <div className="section-header"><h3>Tax Information</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="taxInformation.sacCode" className="form-label">
                SAC Code
              </label>
              <input
                type="text"
                id="taxInformation.sacCode"
                name="taxInformation.sacCode"
                value={formData.taxInformation.sacCode}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                placeholder="Service Accounting Code"
              />
            </div>

            <div className="form-group">
              <label htmlFor="taxInformation.gstRate" className="form-label">
                GST Rate (%)
              </label>
              <input
                type="number"
                id="taxInformation.gstRate"
                name="taxInformation.gstRate"
                value={formData.taxInformation.gstRate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                max="100"
              />
            </div>

            <div className="form-group">
              <label htmlFor="taxInformation.igstRate" className="form-label">
                IGST Rate (%)
              </label>
              <input
                type="number"
                id="taxInformation.igstRate"
                name="taxInformation.igstRate"
                value={formData.taxInformation.igstRate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                max="100"
              />
            </div>

            <div className="form-group">
              <label htmlFor="taxInformation.cgstRate" className="form-label">
                CGST Rate (%)
              </label>
              <input
                type="number"
                id="taxInformation.cgstRate"
                name="taxInformation.cgstRate"
                value={formData.taxInformation.cgstRate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                max="100"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="taxInformation.sgstRate" className="form-label">
              SGST Rate (%)
            </label>
            <input
              type="number"
              id="taxInformation.sgstRate"
              name="taxInformation.sgstRate"
              value={formData.taxInformation.sgstRate}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              min="0"
              max="100"
            />
          </div>

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Additional notes..."
            />
          </div>

          {/* Action Buttons */}
          {!isViewMode && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="btn btn-primary"
                style={{
                  padding: '10px 24px',
                  background: '#292752',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="btn btn-secondary"
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#495057'
                }}
              >
                <RotateCcw size={16} />
                Reset
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default ProcurementServiceMasterForm

